import { LitElement, html, css } from 'lit';
import { sharedStyles } from './base.js';

// <gt-chip> — a neutral category label ("Daily", "Live", "Intraday").
//   tone: default | live | accent
export class GtChip extends LitElement {
  static properties = { tone: { type: String } };
  static styles = [
    sharedStyles,
    css`
      :host {
        display: inline-flex;
      }
      .chip {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 2px 8px;
        border-radius: var(--radius-pill, 999px);
        font-size: var(--fs-label, 0.68rem);
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        background: var(--bg-chip, #1a2136);
        color: var(--muted, #94a3b8);
        border: 1px solid var(--border, #1e2535);
      }
      :host([tone='accent']) .chip {
        color: var(--accent, #c8a84b);
        border-color: color-mix(in srgb, var(--accent, #c8a84b) 40%, transparent);
      }
      :host([tone='live']) .chip {
        color: var(--up, #22c55e);
        border-color: color-mix(in srgb, var(--up, #22c55e) 40%, transparent);
      }
      :host([tone='live']) .dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: var(--up, #22c55e);
        animation: pulse 1.6s ease-in-out infinite;
      }
      @keyframes pulse {
        0%,
        100% {
          opacity: 1;
        }
        50% {
          opacity: 0.35;
        }
      }
    `,
  ];
  render() {
    return html`<span class="chip"
      >${this.tone === 'live' ? html`<span class="dot"></span>` : null
      }<slot></slot
    ></span>`;
  }
}
customElements.define('gt-chip', GtChip);

// <gt-badge> — a semantic colored pill for deltas / 13F diffs.
//   variant: up | down | flat | new | exit | add | trim | neutral
const VARIANT_CSS = css`
  :host([variant='up']) .badge {
    background: color-mix(in srgb, var(--up, #22c55e) 16%, transparent);
    color: var(--up, #22c55e);
  }
  :host([variant='down']) .badge {
    background: color-mix(in srgb, var(--down, #ef4444) 16%, transparent);
    color: var(--down, #ef4444);
  }
  :host([variant='new']) .badge {
    background: var(--badge-new-bg, #134e3a);
    color: var(--badge-new-fg, #34d399);
  }
  :host([variant='exit']) .badge {
    background: var(--badge-exit-bg, #4b1818);
    color: var(--badge-exit-fg, #fca5a5);
  }
  :host([variant='add']) .badge {
    background: var(--badge-add-bg, #133748);
    color: var(--badge-add-fg, #67e8f9);
  }
  :host([variant='trim']) .badge {
    background: var(--badge-trim-bg, #3a2d10);
    color: var(--badge-trim-fg, #fde68a);
  }
`;

export class GtBadge extends LitElement {
  static properties = { variant: { type: String } };
  static styles = [
    sharedStyles,
    css`
      :host {
        display: inline-flex;
      }
      .badge {
        display: inline-flex;
        align-items: center;
        padding: 2px 7px;
        border-radius: var(--radius-sm, 4px);
        font-size: var(--fs-label, 0.68rem);
        font-weight: 700;
        letter-spacing: 0.03em;
        font-family: var(--font-mono, monospace);
        font-variant-numeric: tabular-nums;
        background: var(--bg-chip, #1a2136);
        color: var(--muted, #94a3b8);
      }
    `,
    VARIANT_CSS,
  ];
  render() {
    return html`<span class="badge"><slot></slot></span>`;
  }
}
customElements.define('gt-badge', GtBadge);
